'use client';

/**
 * Magazine cover, modeled on the AI-2027 PDF cover (scripts/cover.typ in the
 * ai2027 repo): a huge Gill Sans wordmark with a dark-red accent, monospace
 * (Menlo) author list, the AI Futures Project lockup top-right, and a
 * publication / design credit footer.
 *
 * All editable cover copy lives in the consts below so an author can retune the
 * kicker, wordmark, credits, and colors from one place.
 */

// The dark red used for the "2040" in the wordmark and the kicker rule. Matches
// the AI-2027 cover's rgb("#8b0000").
export const COVER_ACCENT = '#8b0000';

// The small tracked label above the title.
export const COVER_KICKER = 'AI 2040 · PLAN A SUPPLEMENT';

// Standing credits shown in the cover footer.
export const COVER_PUBLISHER = 'AI Futures Project';
export const COVER_DESIGN_CREDIT = 'Design by\nLightcone Infrastructure';
export const COVER_PUBLISH_LINE = 'A supplement to the AI 2040 scenario\nai-2040.com';

// Step the wordmark down for longer titles so it never overruns the page.
// Short titles ("Verification Plan") stay huge; long ones shrink to fit.
function titleSize(title: string): string {
  const n = title.length;
  if (n <= 18) return '64pt';
  if (n <= 32) return '52pt';
  if (n <= 50) return '40pt';
  return '32pt';
}

export default function PrintCover({
  title,
  author,
  subtitle,
  kicker = COVER_KICKER,
  publishLine = COVER_PUBLISH_LINE,
}: {
  title: string;
  author?: string;
  // A second line under the wordmark (e.g. the chosen plan for the scenario).
  subtitle?: string;
  // Override the small tracked label above the title.
  kicker?: string;
  // Override the bottom-left publication line.
  publishLine?: string;
}) {
  return (
    <section className="print-cover">
      <div className="print-cover-top">
        <div className="print-cover-kicker">{kicker}</div>
        <div className="print-cover-publisher">
          <span>{COVER_PUBLISHER}</span>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/aif-logo.svg" alt="" className="print-cover-logo" />
        </div>
      </div>

      <h1 className="print-cover-title" style={{ fontSize: titleSize(title) }}>
        {title}
      </h1>

      {subtitle ? (
        <div className="print-cover-subtitle">{subtitle}</div>
      ) : null}

      {author ? (
        <div className="print-cover-authors">
          {author.split(/,\s*/).map((name) => (
            <div key={name}>{name}</div>
          ))}
        </div>
      ) : null}

      <div className="print-cover-footer">
        <div className="print-cover-publish">{publishLine}</div>
        <div className="print-cover-design">
          {COVER_DESIGN_CREDIT}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/lightcone-logo.svg"
            alt=""
            className="print-cover-design-logo"
          />
        </div>
      </div>
    </section>
  );
}
